import {useRef, useState, type FormEvent} from 'react'
import {useTranslation} from 'react-i18next'
import {Dialog, DialogContent, DialogHeader, DialogTitle} from '@/components/ui/dialog'
import {Input} from '@/components/ui/input'
import {Label} from '@/components/ui/label'
import {DateField} from '@/components/date-field'
import {FieldError} from '@/components/field-error'
import {FormDialogFooter} from '@/components/form-dialog-footer'
import {
  EVIDENCE_ACCEPT,
  EVIDENCE_ALLOWED_TYPES,
  EVIDENCE_MAX_BYTES,
  evidenceTooBig,
  uploadToStorage,
} from '@/hooks/sgsst/evidencias/use-evidence'
import {mintPlanValidacionUpload, useRegistrarPlanValidacion} from '@/hooks/bateria/use-bateria-plan-validacion'
import {translateApiError} from '@/lib/errors'
import {validateRequired} from '@/lib/validation'

/** G17′ (diseño v2 §0.1). El soporte sigue la política de evidencia SG-SST (mint → PUT → registro) y
 * el registro es informativo: nunca bloquea. */
export function PlanValidacionDialog({open, onOpenChange}: {open: boolean; onOpenChange: (open: boolean) => void}) {
  const {t} = useTranslation()
  const registrar = useRegistrarPlanValidacion()
  const fileRef = useRef<HTMLInputElement>(null)
  const [validadoPor, setValidadoPor] = useState('')
  const [licencia, setLicencia] = useState('')
  const [validadoEn, setValidadoEn] = useState('')
  const [file, setFile] = useState<File | null>(null)
  const [uploading, setUploading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [fieldErrors, setFieldErrors] = useState<{validadoPor?: string; licencia?: string; validadoEn?: string}>({})

  function close() {
    setValidadoPor('')
    setLicencia('')
    setValidadoEn('')
    setFile(null)
    setError(null)
    setFieldErrors({})
    if (fileRef.current) fileRef.current.value = ''
    registrar.reset()
    onOpenChange(false)
  }

  function pickFile(picked: File | null | undefined) {
    if (!picked) return
    if (!EVIDENCE_ALLOWED_TYPES.has(picked.type)) {
      setError(t('errors.evidenceBadFormat'))
      return
    }
    if (picked.size > EVIDENCE_MAX_BYTES) {
      setError(evidenceTooBig(t, picked))
      return
    }
    setError(null)
    setFile(picked)
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    const errors = {
      validadoPor: validateRequired(validadoPor) ?? undefined,
      licencia: validateRequired(licencia) ?? undefined,
      validadoEn: validateRequired(validadoEn) ?? undefined,
    }
    setFieldErrors(errors)
    if (errors.validadoPor || errors.licencia || errors.validadoEn) return
    if (!file) {
      setError(t('admin.bateria.planValidacion.soporteRequerido'))
      return
    }
    setUploading(true)
    setError(null)
    try {
      const target = await mintPlanValidacionUpload(file)
      await uploadToStorage(target, file, () => {})
      await registrar.mutateAsync({
        validado_por: validadoPor.trim(),
        licencia: licencia.trim(),
        validado_en: validadoEn,
        soporte_object_key: target.object_key,
        soporte_size_bytes: file.size,
      })
      close()
    } catch (err) {
      setError(translateApiError(err))
    } finally {
      setUploading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={(next) => !next && close()}>
      <DialogContent className="max-w-lg" data-testid="plan-validacion-dialog">
        <DialogHeader>
          <DialogTitle>{t('admin.bateria.planValidacion.dialogTitle')}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" noValidate>
          <p className="text-sm text-muted-foreground">{t('admin.bateria.planValidacion.dialogIntro')}</p>
          <div className="space-y-2">
            <Label htmlFor="plan-validacion-nombre">{t('admin.bateria.planValidacion.validadoPor')}</Label>
            <Input
              id="plan-validacion-nombre"
              data-testid="plan-validacion-nombre"
              value={validadoPor}
              onChange={(e) => {
                setValidadoPor(e.target.value)
                setFieldErrors((prev) => ({...prev, validadoPor: undefined}))
              }}
              onBlur={() =>
                setFieldErrors((prev) => ({...prev, validadoPor: validateRequired(validadoPor) ?? undefined}))
              }
              aria-invalid={!!fieldErrors.validadoPor}
              maxLength={255}
            />
            <FieldError error={fieldErrors.validadoPor} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="plan-validacion-licencia">{t('admin.bateria.planValidacion.licencia')}</Label>
            <Input
              id="plan-validacion-licencia"
              data-testid="plan-validacion-licencia"
              className="font-mono"
              value={licencia}
              onChange={(e) => {
                setLicencia(e.target.value)
                setFieldErrors((prev) => ({...prev, licencia: undefined}))
              }}
              onBlur={() => setFieldErrors((prev) => ({...prev, licencia: validateRequired(licencia) ?? undefined}))}
              aria-invalid={!!fieldErrors.licencia}
              maxLength={100}
            />
            <FieldError error={fieldErrors.licencia} />
          </div>
          <DateField
            id="plan-validacion-fecha"
            label={t('admin.bateria.planValidacion.validadoEn')}
            value={validadoEn}
            max={new Date().toISOString().slice(0, 10)}
            onChange={(value) => {
              setValidadoEn(value)
              setFieldErrors((prev) => ({...prev, validadoEn: undefined}))
            }}
          />
          <FieldError error={fieldErrors.validadoEn} />
          <div className="space-y-2">
            <Label htmlFor="plan-validacion-soporte">{t('admin.bateria.planValidacion.soporte')}</Label>
            <input
              ref={fileRef}
              id="plan-validacion-soporte"
              data-testid="plan-validacion-soporte"
              type="file"
              accept={EVIDENCE_ACCEPT}
              onChange={(e) => pickFile(e.target.files?.[0])}
              className="block w-full text-sm text-foreground file:mr-3 file:rounded-md file:border-0 file:bg-secondary file:px-3 file:py-1.5 file:text-sm"
            />
            {file && (
              <p className="text-xs text-muted-foreground" data-testid="plan-validacion-soporte-elegido">
                {file.name}
              </p>
            )}
          </div>
          <FormDialogFooter
            error={error}
            pending={uploading || registrar.isPending}
            submitLabel={t('admin.bateria.planValidacion.registrar')}
            onCancel={close}
          />
        </form>
      </DialogContent>
    </Dialog>
  )
}
